import { z } from 'zod';
import { BasicQuery, EntityState, Runtime, TimeFrame } from './webapp-common.dto';

export const Service = z.object({
  id: z.number(),
  name: z.string(),
  totalFunctions: z.number(),
  functionsInvoked: z.number(),
  lastSeenAt: z.coerce.date().nullable(),
  state: EntityState,
  cost: z.number().nonnegative().nullable(),
  runtime: Runtime,
});
export type Service = z.infer<typeof Service>;

export const GetServicesResponse = z.object({
  total: z.number(),
  countFiltered: z.number(), // if no filter is applied, this will be the same as total
  countActiveFiltered: z.number(),
  services: z.array(Service),
});
export type GetServicesResponse = z.infer<typeof GetServicesResponse>;

export const ServiceSortableKeys = Service.pick({
  id: true,
  name: true,
  lastSeenAt: true,
  state: true,
  totalFunctions: true,
  functionsInvoked: true,
  runtime: true,
}).keyof();
export type ServiceSortableKeys = z.infer<typeof ServiceSortableKeys>;

export const GetServicesQuery = BasicQuery.extend({
  orderBy: z
    .object({
      key: ServiceSortableKeys,
      sortOrder: z.union([z.literal('asc'), z.literal('desc')]),
    })
    .optional(),
  runtimes: z.array(Runtime).optional(),
});
export type GetServicesQuery = z.infer<typeof GetServicesQuery>;

export const GetServiceQuery = z.object({
  environmentName: z.string().optional(),
  timeFrame: TimeFrame.optional(),
});
export type GetServiceQuery = z.infer<typeof GetServiceQuery>;

export const GetServiceResponse = z.object({
  service: Service,
});
export type GetServiceResponse = z.infer<typeof GetServiceResponse>;

export const GetAllServicesResponse = z.object({
  services: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
});
export type GetAllServicesResponse = z.infer<typeof GetAllServicesResponse>;

export const ServiceEnvironment = z.object({
  name: z.string(),
  latestReceivedAt: z.coerce.date().nullable(),
});
export type ServiceEnvironment = z.infer<typeof ServiceEnvironment>;

export const ServiceStatus = z.object({
  id: z.number(),
  name: z.string(),
  environments: z.array(ServiceEnvironment),
});
export type ServiceStatus = z.infer<typeof ServiceStatus>;

export const GetServicesStatusResponse = z.object({
  services: z.array(ServiceStatus),
});
export type GetServicesStatusResponse = z.infer<typeof GetServicesStatusResponse>;

export const FirstServiceInfoResponse = z.object({
  serviceId: z.number().optional(),
  environmentName: z.string().optional(),
  latestReceivedAt: z.coerce.date().nullable().optional(),
});
export type FirstServiceInfoResponse = z.infer<typeof FirstServiceInfoResponse>;
